/**
 * SHARED — workspace switcher.
 *
 * Renders NOTHING for the overwhelming majority of users, who belong to exactly
 * one workspace. It appears only when there is a genuine choice to make.
 *
 * Why a full page reload on switch, rather than re-rendering:
 * every service in the app caches its resolved account id in module scope
 * (`let _acct = null` + an `activeAccountId()` helper, repeated in a dozen
 * files) and each exposes its own reset function. Calling all of them from here
 * would mean importing a dozen modules into /shared — the exact coupling
 * MODULES.md forbids — and any service that forgot to export a reset would
 * silently keep serving the previous tenant's data. A reload cannot get that
 * wrong. Switching workspace is a deliberate, rare act; a reload is the honest
 * cost of guaranteeing nothing stale survives it.
 */

import { useEffect, useRef, useState } from "react";
import { listMyAccounts, setActiveAccountId } from "../utils/accountService.js";
import { useSession } from "./SessionProvider.jsx";

export default function WorkspaceSwitcher() {
  const { user } = useSession();
  const [accounts, setAccounts] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let alive = true;
    if (!user) { setAccounts([]); return undefined; }
    // Non-blocking and failure-tolerant: the switcher is a convenience, and a
    // hiccup loading it must never take the whole header down.
    Promise.all([
      listMyAccounts(),
      import("../utils/accountService.js").then((m) => m.getMyPrimaryAccount()),
    ])
      .then(([list, primary]) => {
        if (!alive) return;
        setAccounts(list);
        setActiveId(primary?.accountId ?? null);
      })
      .catch((e) => console.error("[WorkspaceSwitcher]", e));
    return () => { alive = false; };
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  // One workspace (or none) is not a choice — render nothing at all.
  if (!user || accounts.length < 2) return null;

  const active = accounts.find((a) => a.accountId === activeId) ?? accounts[0];

  const choose = (accountId) => {
    setOpen(false);
    if (accountId === active?.accountId) return;
    setActiveAccountId(user.id, accountId);
    // ⚠️ THIS RELOAD IS LOAD-BEARING, not a convenience. Several module
    // services resolve the tenant once and cache it in a module-level
    // `_accountIdPromise` (pos-fulfillment, depletions-inventory,
    // distributor-orders). None of them subscribes to a switch; the reload is
    // what discards those caches. Replace it with an in-place switch and every
    // one of them keeps reading the PREVIOUS workspace until the next refresh.
    window.location.reload();
  };

  return (
    <div className="usermenu" ref={ref}>
      <button
        className="help-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        title="Switch workspace"
        style={{ maxWidth: 220 }}
      >
        <span
          style={{
            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
            fontSize: 13, maxWidth: 150,
          }}
        >
          {active?.name ?? "Workspace"}
        </span>
        <span className="caret" aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="usermenu-pop" role="menu">
          <div className="usermenu-head">
            <div className="uname">Workspace</div>
            <div className="uemail muted">Switching reloads the app.</div>
          </div>
          {accounts.map((a) => (
            <button
              key={a.accountId}
              className="usermenu-item"
              role="menuitemradio"
              aria-checked={a.accountId === active?.accountId}
              onClick={() => choose(a.accountId)}
            >
              <span style={{ display: "inline-block", width: 16 }}>
                {a.accountId === active?.accountId ? "✓" : ""}
              </span>
              {a.name}
              {a.autoCreated && <span className="muted" style={{ fontSize: 11 }}> · personal</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
